'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/app/components/ui/button';
import { Icon } from '@iconify/react';
import Link from 'next/link';
import { useMediaQuery } from '@/app/hooks/use-media-query';

import { Metadata } from 'next';

export const runtime = 'edge';

export const metadata: Metadata = {
  title: 'Resume | Ellis Velandia',
  description: 'View and download the professional resume of Ellis Velandia, Software Engineer.',
  alternates: {
    canonical: '/resume',
  },
  openGraph: {
    title: 'Resume | Ellis Velandia',
    description: 'View and download the professional resume of Ellis Velandia, Software Engineer.',
    url: 'https://ellisvelandia.com/resume',
  },
};

/**
 * Resume page component
 * Displays the PDF resume with options to download
 * Responsive design for mobile, tablet and desktop
 * Includes fallback for mobile devices that don't support PDF embedding well
 */
export default function ResumePage() {
  const [mounted, setMounted] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Define the resume filename
  const resumeFileName = "Ellis_Velandia_CV.pdf";
  const resumePath = `/documents/${resumeFileName}`;

  // Handle client-side mounting for PDF viewer
  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      <div className="flex flex-col space-y-6">
        {/* Header with navigation and actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Link
              href="/"
              className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors mb-2"
            >
              <Icon icon="lucide:arrow-left" className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">My Resume</h1>
          </div>

          <div className="flex space-x-3">
            <Button asChild variant="default" className="gap-2">
              <a href={resumePath} download>
                <Icon icon="lucide:download" className="h-4 w-4" />
                Download PDF
              </a>
            </Button>
            <Button asChild variant="outline" className="gap-2">
              <a href={resumePath} target="_blank" rel="noopener noreferrer">
                <Icon icon="lucide:external-link" className="h-4 w-4" />
                Open in New Tab
              </a>
            </Button>
          </div>
        </div>

        {/* PDF Viewer with mobile fallback */}
        {mounted && (
          <>
            {isMobile ? (
              <div className="flex flex-col items-center justify-center space-y-6 py-10 px-4 bg-muted rounded-lg shadow-md">
                <div className="flex flex-col items-center text-center">
                  <Icon icon="lucide:file-text" className="h-16 w-16 text-muted-foreground mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Mobile PDF Preview</h2>
                  <p className="text-muted-foreground mb-6">
                    PDF preview is limited on mobile devices. For the best experience, please download the PDF or open it in a new tab.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <Button asChild variant="default" className="gap-2 w-full">
                      <a href={resumePath} download>
                        <Icon icon="lucide:download" className="h-4 w-4" />
                        Download PDF
                      </a>
                    </Button>
                    <Button asChild variant="outline" className="gap-2 w-full">
                      <a href={resumePath} target="_blank" rel="noopener noreferrer">
                        <Icon icon="lucide:external-link" className="h-4 w-4" />
                        Open in New Tab
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Preview image of first page */}
                <div className="w-full max-w-sm mx-auto border border-border rounded-lg overflow-hidden shadow-sm">
                  <div className="aspect-[8.5/11] bg-background relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="flex flex-col items-center justify-center p-4 text-center">
                        <Icon icon="lucide:file-text" className="h-20 w-20 text-muted-foreground mb-4" />
                        <p className="text-sm text-muted-foreground">
                          Resume preview not available on mobile.
                          <br />
                          Please download or open in a new tab.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full bg-muted rounded-lg shadow-md overflow-hidden">
                <div className="relative w-full" style={{ height: 'calc(100vh - 200px)', minHeight: '500px' }}>
                  <iframe
                    src={resumePath}
                    className="absolute inset-0 w-full h-full"
                    title="Ellis Velandia Resume"
                  />
                </div>
              </div>
            )}
          </>
        )}

        {!mounted && (
          <div className="w-full bg-muted rounded-lg shadow-md overflow-hidden">
            <div className="flex items-center justify-center" style={{ height: '500px' }}>
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          </div>
        )}

        {/* Additional information */}
        <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
          <h2 className="text-xl font-semibold mb-4">About This Resume</h2>
          <p className="text-muted-foreground mb-4">
            This resume highlights my skills, experience, and qualifications as a Full-Stack Developer.
            Feel free to download it or contact me directly for more information.
          </p>
          <div className="flex flex-wrap gap-3 mt-4">
            <Button asChild variant="outline" size="sm" className="gap-2">
              <Link href="/contact">
                <Icon icon="lucide:mail" className="h-4 w-4" />
                Contact Me
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="gap-2">
              <Link href="/projects">
                <Icon icon="lucide:folder" className="h-4 w-4" />
                View Projects
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
} 